import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

import {
  ArrowCounterClockwise,
  Calculator,
  CheckCircle,
  CircleNotch,
  Clock,
  CurrencyDollar,
  Sparkle,
  TrendUp,
  WarningCircle,
  X
} from '@phosphor-icons/react';
const EditROIModal = ({ isOpen, onClose, opportunity, onSave, organizationId }) => {
  const [annualSavings, setAnnualSavings] = useState('');
  const [implementationCost, setImplementationCost] = useState('');
  const [useAutoCalculation, setUseAutoCalculation] = useState(false);
  const [hourlyRate, setHourlyRate] = useState('');
  const [timeSavedHours, setTimeSavedHours] = useState('');
  const [compensationType, setCompensationType] = useState('hourly'); // 'hourly' or 'annual'
  const [annualSalary, setAnnualSalary] = useState('');
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen && opportunity) {
      console.log('📊 EditROIModal - Opportunity data:', opportunity);
      console.log('📊 hours_saved_per_month:', opportunity.hours_saved_per_month);
      console.log('📊 time_saved:', opportunity.time_saved);
      
      // Set ROI data if available (resilient to both field names)
      if (opportunity.roi) {
        setAnnualSavings((opportunity.roi.annual_savings ?? opportunity.roi.roi_annual)?.toString() || '');
        setImplementationCost(opportunity.roi.effort_cost?.toString() || '');
      }
      
      setHasChanges(false);
      setUseAutoCalculation(false);
      
      // Pre-fill time saved from opportunity (fixed value)
      // Check both possible field names: hours_saved_per_month or time_saved
      const hoursSaved = opportunity.hours_saved_per_month || opportunity.time_saved;
      console.log('📊 Final hoursSaved value:', hoursSaved);
      
      if (hoursSaved) {
        setTimeSavedHours(hoursSaved.toString());
        console.log('✅ Time saved set to:', hoursSaved.toString());
      } else {
        console.log('❌ No hours saved value found in opportunity');
        // Set a default or show error
        setTimeSavedHours('0');
      }
    }
  }, [isOpen, opportunity]);

  // Auto-calculate annual savings based on time saved and compensation
  // Using useMemo to recalculate when dependencies change
  const calculateAnnualSavingsFromTime = useMemo(() => {
    const hours = parseFloat(timeSavedHours) || 0;
    
    if (compensationType === 'hourly') {
      const rate = parseFloat(hourlyRate) || 0;
      // Assuming monthly hours, convert to annual (hours per month * 12)
      return hours * rate * 12;
    } else {
      const salary = parseFloat(annualSalary) || 0;
      // Calculate hourly rate from annual salary (assuming 2080 working hours per year)
      const effectiveHourlyRate = salary / 2080;
      // Convert monthly hours to annual savings
      return hours * effectiveHourlyRate * 12;
    }
  }, [timeSavedHours, hourlyRate, annualSalary, compensationType]);

  // Calculate derived values using useMemo for automatic recalculation
  const calculatedROI = useMemo(() => {
    let savings = 0;
    
    if (useAutoCalculation) {
      savings = calculateAnnualSavingsFromTime;
    } else {
      savings = parseFloat(annualSavings) || 0;
    }
    
    const cost = parseFloat(implementationCost) || 0;
    
    const netValue = savings - cost;
    const roiPercentage = cost > 0 ? ((savings - cost) / cost) * 100 : 0;
    
    // Determine status based on ROI percentage
    let status = 'Unknown';
    if (roiPercentage >= 300) status = 'Excellent';
    else if (roiPercentage >= 150) status = 'Good';
    else if (roiPercentage >= 50) status = 'Moderate';
    else if (roiPercentage > 0) status = 'Poor';
    else status = 'Negative';
    
    return {
      annual_savings: savings,
      effort_cost: cost,
      roi_value: netValue,
      roi_percentage: roiPercentage,
      status
    };
  }, [useAutoCalculation, calculateAnnualSavingsFromTime, annualSavings, implementationCost]);

  const handleInputChange = (setter) => (e) => {
    // Only allow numbers and decimal point
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setter(value);
    setHasChanges(true);
    setError(null);
  };

  const handleAnalyzeWithAI = async () => {
    try {
      setAnalyzing(true);
      setError(null);

      // Validate compensation is entered
      if (!hourlyRate && !annualSalary) {
        setError('Please enter hourly rate or annual salary first');
        return;
      }

      // Call backend to analyze opportunity
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/opportunities/${opportunity.id}/analyze-hours`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        },
        organizationId
      );

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI Analysis result:', data);
        
        // Set the AI-calculated hours saved
        setTimeSavedHours(data.hours_saved_per_month.toString());
        setHasChanges(true);
        
        // Show success message with reasoning
        if (data.reasoning) {
          console.log('AI Reasoning:', data.reasoning);
        }
      } else {
        throw new Error('Failed to analyze opportunity');
      }
    } catch (err) {
      console.error('Error analyzing opportunity:', err);
      setError(err.message || 'Failed to analyze opportunity. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const savings = parseFloat(annualSavings);
      const cost = parseFloat(implementationCost);

      if (isNaN(savings) || savings < 0) {
        setError('Please enter a valid annual savings amount');
        return;
      }

      if (isNaN(cost) || cost < 0) {
        setError('Please enter a valid implementation cost');
        return;
      }

      await onSave(calculatedROI);
      onClose();
    } catch (err) {
      console.error('Error saving ROI:', err);
      setError(err.message || 'Failed to save ROI estimates. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = () => {
    if (opportunity) {
      if (opportunity.roi) {
        setAnnualSavings((opportunity.roi.annual_savings ?? opportunity.roi.roi_annual)?.toString() || '');
        setImplementationCost(opportunity.roi.effort_cost?.toString() || '');
      }
      setUseAutoCalculation(false);
      setHourlyRate('');
      setAnnualSalary('');
      // Restore time saved from opportunity
      const hoursSaved = opportunity.hours_saved_per_month || opportunity.time_saved;
      if (hoursSaved) {
        setTimeSavedHours(hoursSaved.toString());
      }
      setHasChanges(false);
      setError(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Excellent': return 'text-[#14F195]';
      case 'Good': return 'text-[#10B981]';
      case 'Moderate': return 'text-[#F59E0B]';
      case 'Poor':
      case 'Negative': return 'text-[#EF4444]';
      default: return 'text-[#A0AEC0]';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'Excellent': return 'bg-[#14F195]/10 border-[#14F195]/30';
      case 'Good': return 'bg-[#10B981]/10 border-[#10B981]/30';
      case 'Moderate': return 'bg-[#F59E0B]/10 border-[#F59E0B]/30';
      case 'Poor':
      case 'Negative': return 'bg-[#EF4444]/10 border-[#EF4444]/30';
      default: return 'bg-[#A0AEC0]/10 border-[#A0AEC0]/30';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl max-h-[90vh] bg-[#1a1a1a] rounded-2xl border border-[rgba(255,255,255,0.1)] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.1)] flex-shrink-0">
          <div className="flex items-center gap-3">
            <CurrencyDollar size={24} className="text-[#6366F1]" />
            <div>
              <h2 className="text-xl font-bold text-white">Edit ROI Estimates</h2>
              <p className="text-sm text-gray-400">Manually override system-generated estimates</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <WarningCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-500 font-semibold">Error</p>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Calculation Method Toggle */}
          <div className="flex items-center justify-between p-4 bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.3)] rounded-lg">
            <div className="flex items-center gap-3">
              <Calculator size={20} className="text-[#6366F1]" />
              <div>
                <p className="text-white font-semibold text-sm">Auto-Calculate from Role Data</p>
                <p className="text-gray-400 text-xs">Calculate savings based on time saved and compensation</p>
              </div>
            </div>
            <button
              onClick={() => {
                setUseAutoCalculation(!useAutoCalculation);
                setHasChanges(true);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                useAutoCalculation ? 'bg-[#6366F1]' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  useAutoCalculation ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Input Fields */}
          <div className="space-y-4">
            {useAutoCalculation ? (
              <>
                {/* Compensation Type Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Compensation Type</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setCompensationType('hourly');
                        setHasChanges(true);
                      }}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        compensationType === 'hourly'
                          ? 'bg-[#6366F1] text-white'
                          : 'bg-[rgba(255,255,255,0.05)] text-gray-400 hover:bg-[rgba(255,255,255,0.1)]'
                      }`}
                    >
                      Hourly Rate
                    </button>
                    <button
                      onClick={() => {
                        setCompensationType('annual');
                        setHasChanges(true);
                      }}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        compensationType === 'annual'
                          ? 'bg-[#6366F1] text-white'
                          : 'bg-[rgba(255,255,255,0.05)] text-gray-400 hover:bg-[rgba(255,255,255,0.1)]'
                      }`}
                    >
                      Annual Salary
                    </button>
                  </div>
                </div>

                {/* Hourly Rate or Annual Salary Input */}
                {compensationType === 'hourly' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Hourly Rate ($)
                      <span className="text-gray-500 font-normal ml-2">- Role-specific hourly compensation</span>
                    </label>
                    <div className="relative">
                      <CurrencyDollar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={hourlyRate}
                        onChange={handleInputChange(setHourlyRate)}
                        placeholder="e.g., 50"
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Annual Salary ($)
                      <span className="text-gray-500 font-normal ml-2">- Role-specific yearly compensation</span>
                    </label>
                    <div className="relative">
                      <CurrencyDollar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={annualSalary}
                        onChange={handleInputChange(setAnnualSalary)}
                        placeholder="e.g., 80000"
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* AI Analyze Button */}
                <div>
                  <button
                    onClick={handleAnalyzeWithAI}
                    disabled={analyzing || (!hourlyRate && !annualSalary)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {analyzing ? (
                      <>
                        <CircleNotch size={16} className="animate-spin" />
                        <span className="font-semibold">Analyzing...</span>
                      </>
                    ) : (
                      <>
                        <Sparkle size={16} />
                        <span className="font-semibold">Analyze with AI</span>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    {!hourlyRate && !annualSalary 
                      ? 'Enter compensation first, then AI will calculate time saved + ROI'
                      : 'AI will analyze this opportunity and calculate time saved + ROI'
                    }
                  </p>
                </div>

                {/* Show results after AI analysis */}
                {timeSavedHours && parseFloat(timeSavedHours) > 0 && (
                  <div className="space-y-3">
                    {/* AI-Calculated Time Saved */}
                    <div className="p-3 bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.3)] rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkle size={14} className="text-[#6366F1]" />
                          <span className="text-sm text-gray-300">AI-Calculated Time Saved:</span>
                        </div>
                        <span className="text-base font-bold text-white">
                          {timeSavedHours} hours/month
                        </span>
                      </div>
                    </div>

                    {/* Calculated Annual Savings */}
                    <div className="p-3 bg-[rgba(16,185,129,0.1)] border border-[rgba(16,185,129,0.3)] rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-300">Annual Savings:</span>
                        <span className="text-lg font-bold text-[#10B981]">
                          ${calculateAnnualSavingsFromTime.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Manual Annual Savings Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Annual Savings ($)
                    <span className="text-gray-500 font-normal ml-2">- Expected yearly cost reduction</span>
                  </label>
                  <div className="relative">
                    <CurrencyDollar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={annualSavings}
                      onChange={handleInputChange(setAnnualSavings)}
                      placeholder="e.g., 50000"
                      className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Implementation Cost (always shown) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Implementation Cost ($)
                <span className="text-gray-500 font-normal ml-2">- One-time setup and training cost</span>
              </label>
              <div className="relative">
                <CurrencyDollar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={implementationCost}
                  onChange={handleInputChange(setImplementationCost)}
                  placeholder="e.g., 10000"
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>

          {/* Calculated ROI Preview */}
          <div className={`p-3 rounded-lg border ${getStatusBg(calculatedROI.status)}`}>
            <div className="flex items-center gap-2 mb-2">
              <Sparkle size={14} className={getStatusColor(calculatedROI.status)} />
              <span className="text-sm font-semibold text-white">Calculated ROI</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Status</p>
                <p className={`text-base font-bold ${getStatusColor(calculatedROI.status)}`}>
                  {calculatedROI.status}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">ROI Percentage</p>
                <p className={`text-base font-bold ${getStatusColor(calculatedROI.status)}`}>
                  {calculatedROI.roi_percentage.toFixed(0)}%
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Net Value</p>
                <p className={`text-base font-bold ${calculatedROI.roi_value >= 0 ? 'text-[#14F195]' : 'text-[#EF4444]'}`}>
                  ${calculatedROI.roi_value.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Payback Period</p>
                <p className="text-base font-bold text-white">
                  {calculatedROI.annual_savings > 0 
                    ? `${(calculatedROI.effort_cost / calculatedROI.annual_savings * 12).toFixed(1)} mo`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[rgba(255,255,255,0.1)] bg-[rgba(0,0,0,0.3)] flex-shrink-0">
          <button
            onClick={handleRestoreDefaults}
            disabled={!hasChanges || saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowCounterClockwise size={16} />
            Restore Original
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <CircleNotch size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EditROIModal;
